import { z } from 'zod';
import { abilityNameSchema } from '../content/common.js';
import { contentTypeSchema } from '../content/common.js';
import { calcContextSchema } from '../rules/context.js';
import { inventoryActionSchema, inventorySchema } from '../rules/inventory.js';
import { combatSchema, hpActionSchema } from '../rules/combat.js';
import { sheetSpellSchema, spellActionSchema, spellbookSchema, } from '../rules/spells.js';
import { rollEntrySchema, rollOutcomeSchema, rollReasonSchema } from '../rules/dice.js';
import { contentIdSchema, pathSchema } from '../rules/content-id.js';
import { provenanceSchema } from '../rules/provenance.js';
import { activeConditionSchema, xpStateSchema } from '../rules/progress.js';
import { resourceActionSchema, sheetResourceSchema } from '../rules/resources.js';
import { characterCodeSchema } from './code.js';
import { creationChoicesSchema, slotPickSchema } from './choices.js';
import { creationProblemSchema } from './problems.js';
/**
 * Персонаж на границе клиент-сервер: вход создания, сводка и лист.
 *
 * Лист не хранится и приходит только свёрткой журнала (D4). Поэтому
 * в ответе он вместе с провенансом: величина без объяснения, откуда
 * она взялась, в этом проекте не считается посчитанной (D5).
 */
const uuid = z.string().uuid();
/**
 * Устройство, с которого работают до регистрации.
 *
 * Аккаунтов ещё нет (D8: они появятся у мастера в фазе 3), а драфту
 * нужен владелец — иначе чужой драфт открывается по ссылке. Признак
 * тот же, что D8 закладывает под гостевой доступ, `device_token`:
 * случайная строка, которую клиент заводит себе сам и хранит локально.
 *
 * Это не аутентификация и не выдаётся за неё: устройство не человек,
 * восстановить драфт после чистки хранилища нельзя, а тот, кто прочтёт
 * хранилище браузера, получит и драфты. Для персонажа без кампании
 * и без личных данных этого достаточно; всё, что серьёзнее, приходит
 * вместе с регистрацией.
 *
 * Шестнадцать байт: столько же, сколько в uuid, и по той же причине —
 * подобрать перебором нельзя.
 */
export const deviceTokenSchema = z.string().regex(/^[0-9a-f]{32}$/);
/** Заголовок, которым устройство представляется серверу. */
export const DEVICE_TOKEN_HEADER = 'x-rollcall-device';
export const createCharacterInputSchema = z.strictObject({
    /**
     * Место игрока, за которым закрепляется персонаж (domain-model.md);
     * `null` — драфт: персонаж без кампании (D29). Владельцем драфта
     * становится устройство из заголовка, а не значение из тела запроса:
     * иначе достаточно подставить чужой токен.
     */
    seatId: uuid.nullable(),
    choices: creationChoicesSchema,
});
/**
 * Описание персонажа: то, что игрок пишет про него словами.
 *
 * Это данные персонажа, а не механика. В расчёте они не участвуют
 * и в журнал решений не идут: журнал хранит выборы (D4), а «шрам
 * через бровь» выбором не является. Поэтому поля лежат строкой
 * персонажа, рядом с именем.
 *
 * Шесть полей, а не одно «описание»: пустое поле с подписью
 * «Слабость» — это вопрос, на который человек однажды ответит,
 * а пустая простыня — это пустая простыня.
 */
export const characterDetailsSchema = z.strictObject({
    /** Как выглядит. */
    appearance: z.string().max(2000),
    /** Фишка: чем запоминается за столом. */
    quirk: z.string().max(2000),
    /** Причина: зачем пошёл в приключение. */
    motivation: z.string().max(2000),
    /** Прошлое: что было до первой сессии. */
    backstory: z.string().max(4000),
    /** Слабость: за что его можно взять. */
    weakness: z.string().max(2000),
    /** Свободные заметки: всё остальное. */
    notes: z.string().max(4000),
});
/** Пустое описание: у персонажа, которого ещё не описывали. */
export const EMPTY_DETAILS = {
    appearance: '',
    quirk: '',
    motivation: '',
    backstory: '',
    weakness: '',
    notes: '',
};
/**
 * Правка имени и описания (D4).
 *
 * Имя до сих пор задавалось один раз в визарде и больше не менялось,
 * а меняют его постоянно: опечатка в первый вечер, прозвище, данное
 * на третьей сессии. Менять может владелец персонажа и мастер стола,
 * за которым персонаж сидит, — те же двое, что двигают хиты (D3).
 *
 * Имя необязательное: правка одного описания не обязана присылать имя
 * и рисковать затереть его пустой строкой.
 */
export const updateCharacterInputSchema = z.strictObject({
    characterId: uuid,
    name: z.string().min(1).max(60).nullable(),
    details: characterDetailsSchema.nullable(),
});
export const characterSummarySchema = z.strictObject({
    id: uuid,
    name: z.string().min(1),
    /** Описание персонажа: строка персонажа, а не свёртка журнала. */
    details: characterDetailsSchema,
    /** Кампания и место либо оба есть, либо это драфт и обоих нет (D29). */
    campaignId: uuid.nullable(),
    seatId: uuid.nullable(),
    /** Код персонажа: по нему мастер подберёт драфт. */
    code: characterCodeSchema,
    createdAt: z.string(),
});
/**
 * Как называется источник, на который ссылается провенанс.
 *
 * Без этого разбор величины показывает `srd:chain_mail` — ключ, который
 * ничего не говорит игроку. Имена приходят с листом, а не подтягиваются
 * клиентом отдельным запросом: они нужны ровно в момент показа разбора.
 */
export const sheetSourceSchema = z.strictObject({
    id: contentIdSchema,
    name: z.string().min(1),
    type: contentTypeSchema,
});
/** Спасбросок: путь величины и отметка владения. */
export const sheetSaveSchema = z.strictObject({
    ability: abilityNameSchema,
    path: pathSchema,
    proficient: z.boolean(),
});
/** Навык: запись контента, характеристика проверки и владение. */
export const sheetSkillSchema = z.strictObject({
    id: contentIdSchema,
    name: z.string().min(1),
    ability: abilityNameSchema,
    path: pathSchema,
    proficient: z.boolean(),
});
/**
 * Особенность листа: черта вида, предыстория, особенность класса.
 *
 * Текст — дословный из записи контента, по-английски; `textRu` —
 * наш перевод, если он есть (D32). Оригинал не заменяется, а лежит
 * рядом: лицензия требует, чтобы переработку было видно, а игроку
 * бывает нужно свериться с источником.
 */
export const sheetFeatureSchema = z.strictObject({
    key: z.string().min(1),
    name: z.string().min(1),
    text: z.string(),
    textRu: z.string().nullable(),
    source: contentIdSchema,
    /** Уровень класса, на котором особенность получена; `null` — не класс. */
    level: z.number().int().positive().nullable(),
    /**
     * Счётчики этой особенности (D56). Здесь, а не общим списком
     * в стороне: ярость видна под «Rage», и искать её по листу
     * не приходится.
     */
    resources: z.array(sheetResourceSchema),
});
export const characterSheetSchema = z.strictObject({
    character: characterSummarySchema,
    /** Уровень, на который собран лист: пока всегда первый. */
    level: z.number().int().positive(),
    /** Характеристики и состояние, с которыми считали. */
    context: calcContextSchema,
    /** Величина листа → её провенанс, в котором лежит и значение. */
    values: z.record(pathSchema, provenanceSchema),
    /** Имена источников для разбора величин: ключ записи → как она зовётся. */
    sources: z.record(contentIdSchema, sheetSourceSchema),
    /** Спасброски: шесть, по одному на характеристику. */
    saves: z.array(sheetSaveSchema),
    /** Навыки: все, какие есть в контенте, а не только те, чем владеют. */
    skills: z.array(sheetSkillSchema),
    /** Черты и особенности с текстом — то, что эффектами не выражается. */
    features: z.array(sheetFeatureSchema),
    /**
     * Счётчики ресурсов целиком (D56): и те, что стоят под своими
     * особенностями, и свои, заведённые руками. Полный список нужен
     * командам — по нему проверяется трата, — а на экране ресурс
     * всё равно показывается там, где живёт его особенность.
     */
    resources: z.array(sheetResourceSchema),
    /**
     * Инвентарь: что есть, что надето, сколько весит. Такая же свёртка
     * журнала, как и величины листа (D4), поэтому приходит вместе с ним,
     * а не отдельным запросом: показывать их врозь означало бы показать
     * КД от доспеха, которого в списке ещё нет.
     */
    inventory: inventorySchema,
    /** Бой: хиты и атаки. Той же свёртки, что и величины листа (D4). */
    combat: combatSchema,
    /** Заклинания: ячейки, подготовленное, сосредоточение. */
    spells: spellbookSchema,
    /**
     * Опыт и близость повышения (D31). Приходит с листом, а не
     * отдельным запросом: трекер стоит под именем персонажа, и второй
     * запрос ради двух чисел означал бы, что имя и опыт приезжают
     * порознь и мигают.
     */
    xp: xpStateSchema,
    /** Состояния, висящие сейчас: их вешает мастер командой (D3). */
    conditions: z.array(activeConditionSchema),
    /**
     * Черта происхождения (D58): её даёт предыстория, а не выбор игрока,
     * поэтому в журнале её нет, а среди особенностей листа она не стоит.
     * Печатному листу (D64) она нужна строкой бланка. `null` — у
     * предыстории черты нет; поля нет — лист собран до этого поля.
     */
    originFeat: z
        .strictObject({ id: contentIdSchema, name: z.string().min(1) })
        .nullable()
        .optional(),
});
export const createCharacterResultSchema = z.strictObject({
    character: characterSummarySchema,
});
/* ── Правка выборов создания (D62) ───────────────────────────────── */
export const creationEditInputSchema = z.strictObject({ characterId: uuid });
/**
 * Последняя заявка на правку персонажа за столом (D62).
 *
 * Игрок должен видеть, что правка ушла, и чем она кончилась —
 * в том числе почему отказали: отказ без причины оставляет гадать,
 * что чинить (D61).
 */
export const editRequestStateSchema = z.strictObject({
    requestId: uuid,
    status: z.enum(['pending', 'approved', 'rejected']),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/**
 * Что нужно визарду, чтобы открыться на правку: сделанные выборы
 * и где сидит персонаж.
 *
 * Выборы восстанавливаются из шага журнала, а не хранятся рядом:
 * журнал — единственный источник (D4).
 */
export const creationEditSchema = z.strictObject({
    choices: creationChoicesSchema,
    /** Место за столом; `null` — драфт, правка применяется сразу. */
    seatId: uuid.nullable(),
    /**
     * Уровень персонажа. Выше первого класс не меняется: от него
     * зависят шаги повышений (D62).
     */
    level: z.number().int().min(1),
    request: editRequestStateSchema.nullable(),
});
export const editCreationInputSchema = z.strictObject({
    characterId: uuid,
    choices: creationChoicesSchema,
});
/**
 * Итог правки: драфт поправлен сразу, у персонажа за столом
 * правка ушла заявкой (D62).
 */
export const editCreationResultSchema = z.discriminatedUnion('status', [
    z.strictObject({ status: z.literal('applied') }),
    z.strictObject({ status: z.literal('pending'), requestId: uuid }),
]);
/**
 * Что лежит в заявке на правку (D62): новые выборы и записи игрока,
 * на которые они опираются.
 *
 * Записи перечислены при подаче, когда их принадлежность игроку
 * проверена; при подтверждении столу переходят ровно они.
 */
export const editPayloadSchema = z.strictObject({
    choices: creationChoicesSchema,
    records: z.array(contentIdSchema),
});
/**
 * Отказ создания: список замечаний по слотам. Отдельная схема, потому
 * что этот ответ читает интерфейс и подсвечивает по нему слоты.
 */
export const creationRejectionSchema = z.strictObject({
    problems: z.array(creationProblemSchema).min(1),
});
/**
 * Список персонажей: кампания плюс драфты этого устройства (D29).
 *
 * Кампании может не быть вовсе — тогда список состоит из одних
 * драфтов. Устройство в запросе не передаётся: оно приходит
 * заголовком, и подставить чужое из тела нельзя.
 */
export const characterListInputSchema = z.strictObject({
    campaignId: uuid.nullable(),
});
export const characterListOutputSchema = z.array(characterSummarySchema);
export const characterSheetInputSchema = z.strictObject({
    characterId: uuid,
});
export const creationPlanInputSchema = z.strictObject({
    species: z.string().optional(),
    class: z.string().optional(),
    background: z.string().optional(),
    /**
     * Уже сделанные выборы: выбор порождает выбор (D58).
     *
     * Вид даёт черту происхождения, выбранная черта даёт три владения,
     * и слоты этих владений появляются только после того, как черта
     * выбрана. Поэтому плану нужны не три структурных выбора, а все.
     *
     * Клиент шлёт их целиком и перезапрашивает план не на каждую
     * отметку, а когда поменялся выбор в слоте, который умеет
     * порождать новые (`expands` у слота).
     */
    picks: z.record(z.string(), z.array(z.string())).default({}),
    /**
     * Место, для которого собирается персонаж; `null` — драфт.
     *
     * Плану оно нужно не ради проверки прав, а ради контента: записи
     * мастера попадают в визард по флагу той кампании, за столом
     * которой персонажа и заводят (D30, D53).
     */
    seatId: uuid.nullable().default(null),
});
/**
 * Команда инвентаря (D3): клиент шлёт «надень кольчугу», а не «КД
 * теперь 16». Что из этого следует, считает сервер, и ответом
 * приходит пересчитанный лист целиком.
 */
export const inventoryCommandInputSchema = z.strictObject({
    characterId: uuid,
    action: inventoryActionSchema,
});
export const inventoryCommandResultSchema = z.strictObject({
    sheet: characterSheetSchema,
});
/**
 * Изменение хитов — команда (D3): «получил 7 урона», а не «хитов
 * теперь 6». Ответом приходит пересчитанный лист целиком.
 */
export const hpCommandInputSchema = z.strictObject({
    characterId: uuid,
    action: hpActionSchema,
});
export const hpCommandResultSchema = z.strictObject({ sheet: characterSheetSchema });
export const spellCommandInputSchema = z.strictObject({
    characterId: uuid,
    action: spellActionSchema,
});
export const spellCommandResultSchema = z.strictObject({ sheet: characterSheetSchema });
/**
 * Отдых. Долгий возвращает хиты, кости и ячейки; короткий —
 * только ячейки магии договора, а хиты в нём возвращают костями
 * (SRD 5.2, с. 185, 187).
 */
/**
 * Команда со счётчиком ресурса (D3, D56): трата, возврат, свой
 * счётчик и его удаление. Ответ — весь лист, как у хитов: счётчик
 * приходит той же свёрткой, что и остальное состояние.
 */
export const resourceCommandInputSchema = z.strictObject({
    characterId: uuid,
    action: resourceActionSchema,
});
export const restCommandInputSchema = z.strictObject({
    characterId: uuid,
    kind: z.enum(['long', 'short']),
});
/**
 * Список заклинаний класса — то, из чего готовят. Отдельным запросом,
 * а не в листе: у волшебника это сотня записей с описаниями, и возить
 * их при каждом открытии листа незачем.
 */
export const classSpellsInputSchema = z.strictObject({ characterId: uuid });
export const classSpellsOutputSchema = z.array(sheetSpellSchema);
/**
 * Бросок — команда серверу (D12, D3): случайность живёт там, где её
 * видно всем, а не в браузере одного игрока.
 *
 * Персонажа может не быть вовсе: характеристики бросают до того,
 * как он создан. Такой бросок в журнал не ложится — журнал у персонажа.
 */
export const rollCommandInputSchema = z.strictObject({
    characterId: uuid.nullable(),
    reason: rollReasonSchema,
    /** Своя запись для произвольного броска: `2d6+3`. */
    notation: z.string().max(32).nullable(),
    mode: z.enum(['normal', 'advantage', 'disadvantage']),
    /** Результат настоящих костей: игрок бросил их сам (D12). */
    manual: z.number().int().nullable(),
    /** Сколько раз кинуть: шесть — это пул характеристик одним действием. */
    times: z.number().int().min(1).max(12),
    /**
     * Скрытый бросок мастера (D12).
     *
     * Результат виден только ему, а в общем журнале остаётся отметка,
     * что бросок был. Признак приходит командой, но проверяется
     * сервером: скрыть бросок может мастер кампании и только он.
     */
    hidden: z.boolean().default(false),
});
export const rollCommandResultSchema = z.strictObject({
    outcomes: z.array(rollOutcomeSchema),
    /** Записи журнала; пусто, если персонажа ещё нет. */
    entries: z.array(rollEntrySchema),
});
/**
 * Начисление опыта себе (D31).
 *
 * У драфта мастера нет, а опыт за игру он всё равно получает: это
 * единственный случай, когда человек меняет своё число сам. У места
 * в кампании начисляет мастер — там эта процедура отвечает отказом.
 */
export const awardXpInputSchema = z.strictObject({
    characterId: uuid,
    amount: z.number().int(),
});
export const rollLogInputSchema = z.strictObject({
    characterId: uuid,
    limit: z.number().int().min(1).max(100),
});
export const rollLogOutputSchema = z.array(rollEntrySchema);
/** План повышения уровня: что придёт само и что предстоит выбрать (D31). */
export const levelPlanInputSchema = z.strictObject({ characterId: uuid });
/**
 * Повышение уровня — команда (D3): клиент шлёт выборы, сервер
 * дописывает шаг журнала и возвращает пересчитанный лист.
 *
 * Хиты за уровень приходят одним из трёх путей (с. 23, D12):
 * фиксированное значение из записи класса, бросок кости на сервере
 * или число, которое игрок бросил настоящей костью.
 */
export const levelUpCommandInputSchema = z.strictObject({
    characterId: uuid,
    picks: z.array(slotPickSchema),
    increase: z.array(z.strictObject({ ability: abilityNameSchema, amount: z.number().int().positive() })),
    hitPoints: z.discriminatedUnion('kind', [
        z.strictObject({ kind: z.literal('fixed') }),
        z.strictObject({ kind: z.literal('roll') }),
        z.strictObject({ kind: z.literal('manual'), amount: z.number().int().positive() }),
    ]),
});
export const levelUpCommandResultSchema = z.strictObject({
    sheet: characterSheetSchema,
});
/** Откат последнего повышения, пока за ним ничего не записано (D31). */
export const levelDownCommandInputSchema = z.strictObject({ characterId: uuid });
//# sourceMappingURL=character.js.map